import { Card } from '@/ui/Card';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { estadoDelPorcentaje, useAprendizaje, useTendenciaDeAprendizaje } from './api';
import {
  FilaDeArea, GraficoDeMeses, Indicador, Indicadores, ListaDeAlertas, Titulo,
} from './piezas';

/**
 * Aprendizaje — cómo va la formación: finalización, quiz, certificados, la
 * tendencia de los últimos meses, el avance por área y los cursos con más
 * gente.
 *
 * ## Lo que cambia
 *
 * - **Los cursos más populares son enlaces.** Eran un `<div onclick>` con
 *   `APP.navigate`: sin foco, sin teclado y sin poder abrirse en otra pestaña.
 * - El resumen y la tendencia son **dos consultas con su propio estado**: si
 *   falla una, la otra se sigue viendo. El vanilla las pedía juntas y un fallo
 *   de cualquiera dejaba la pestaña entera en el mensaje de error.
 */
export function Aprendizaje({ activa }: { activa: boolean }) {
  const resumen = useAprendizaje(activa);
  const tendencia = useTendenciaDeAprendizaje(activa);
  const d = resumen.data;

  if (resumen.isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (resumen.isError) {
    return (
      <EstadoError
        err={resumen.error}
        que="la analítica de aprendizaje"
        onReintentar={() => void resumen.refetch()}
      />
    );
  }
  if (!d) return null;

  const o = d.overview;
  const certificados = o.cert_summary;

  return (
    <div className="flex flex-col gap-6">
      <Indicadores>
        <Indicador
          icono="bi-mortarboard-fill" valor={`${o.completion_rate} %`} etiqueta="Finalización"
          nota={`${o.completed} de ${o.total_enrollments} inscripciones`}
        />
        <Indicador
          icono="bi-patch-question-fill" valor={`${o.approval_rate} %`} etiqueta="Aprobación del quiz"
          nota={`${o.avg_attempts} intentos de media`}
        />
        <Indicador
          icono="bi-shield-check"
          estado={estadoDelPorcentaje(o.mandatory_compliance)}
          valor={o.mandatory_compliance == null ? '—' : `${o.mandatory_compliance} %`}
          etiqueta="Cumplimiento obligatorio"
          nota="cursos marcados como obligatorios"
        />
        <Indicador
          icono="bi-award-fill" valor={certificados.total} etiqueta="Certificados"
          nota={`${certificados.vigente} vigentes · ${certificados.vencido} vencidos`}
        />
        <Indicador
          icono="bi-play-circle-fill" valor={o.in_progress} etiqueta="En progreso"
          nota={`${o.courses_published} cursos publicados`}
        />
        <Indicador
          icono="bi-people-fill" valor={o.active_users} etiqueta="Usuarios activos"
          nota={`${o.learning_paths} rutas · ${o.competencies} competencias`}
        />
      </Indicadores>

      <section aria-labelledby="apr-alertas">
        <Titulo icono="bi-bell-fill"><span id="apr-alertas">Alertas</span></Titulo>
        <ListaDeAlertas alertas={d.alerts} />
      </section>

      <section aria-labelledby="apr-tendencia">
        <Titulo icono="bi-bar-chart-line-fill"><span id="apr-tendencia">Tendencia</span></Titulo>
        <Card className="p-4">
          {tendencia.isPending ? (
            <Skeleton className="h-44 w-full" />
          ) : tendencia.isError ? (
            <EstadoError
              err={tendencia.error}
              que="la tendencia"
              onReintentar={() => void tendencia.refetch()}
            />
          ) : (
            <GraficoDeMeses
              titulo={`Inscripciones y finalizaciones de los últimos ${tendencia.data?.series.length ?? 0} meses`}
              nombreA="Inscripciones"
              nombreB="Finalizaciones"
              serie={(tendencia.data?.series ?? []).map((s) => ({
                label: s.label, a: s.enrollments, b: s.completions,
              }))}
            />
          )}
        </Card>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section aria-labelledby="apr-areas">
          <Titulo icono="bi-diagram-2-fill"><span id="apr-areas">Avance por área</span></Titulo>
          {d.by_area.length ? (
            <Card className="p-4">
              <ul className="flex flex-col gap-3">
                {d.by_area.map((a) => (
                  <FilaDeArea
                    key={a.area}
                    nombre={a.area}
                    pct={a.completion_rate}
                    estado={estadoDelPorcentaje(a.completion_rate)}
                    derecha={`${Math.round(a.completion_rate)} %`}
                    nota={`${a.completed}/${a.enrollments} · avance medio ${Math.round(a.avg_progress)} %`}
                  />
                ))}
              </ul>
            </Card>
          ) : (
            <EstadoVacio compacto icono="bi-diagram-2" titulo="Sin inscripciones todavía" />
          )}
        </section>

        <section aria-labelledby="apr-cursos">
          <Titulo icono="bi-trophy-fill"><span id="apr-cursos">Cursos con más gente</span></Titulo>
          {d.top_courses.length ? (
            <ol className="flex flex-col gap-2">
              {d.top_courses.map((c, i) => (
                <li key={c.id}>
                  {/* Un enlace, no un `<div onclick>`: recibe foco, se abre en
                      otra pestaña y el Atrás vuelve aquí. */}
                  <a
                    href={`#course-detail/${c.id}`}
                    className="flex items-center gap-3 rounded-sm border border-line bg-sunken px-3 py-2 hover:border-line-strong focus-visible:focus-anillo"
                  >
                    <span className="w-5 shrink-0 text-center text-4 font-extrabold text-muted">{i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <strong className="block truncate text-3 text-ink">{c.title}</strong>
                      <span className="block text-1 text-muted">
                        <i className="bi bi-people" aria-hidden="true" /> {c.enrollments} ·{' '}
                        {Math.round(c.completion_rate)} % de finalización
                      </span>
                    </span>
                    <i className="bi bi-chevron-right shrink-0 text-muted" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ol>
          ) : (
            <EstadoVacio compacto icono="bi-journal" titulo="Sin cursos todavía" />
          )}
        </section>
      </div>
    </div>
  );
}
